import React, { useState } from 'react';
import { tierOptions, promoTypeOptions } from '../promotionsTypes';
import { usePromotionsStore } from '../../../../store/promotionsStore';
import styles from './AddPromotionModal.module.css';

interface AddPromotionModalProps {
  open: boolean;
  onClose: () => void;
}

export const AddPromotionModal: React.FC<AddPromotionModalProps> = ({ open, onClose }) => {
  const [name, setName] = useState('');
  const [discount, setDiscount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [tier, setTier] = useState('');
  const [minQuantity, setMinQuantity] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const create = usePromotionsStore((s) => s.create);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!name.trim() || !discount || !startDate || !endDate || !selectedType) {
      setSubmitError('Name, discount, dates, and type are required');
      return;
    }
    const parsedDiscount = parseFloat(discount.replace(/[^0-9.]/g, ''));
    if (isNaN(parsedDiscount) || parsedDiscount < 0) {
      setSubmitError('Discount must be a positive number');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await create({
        name: name.trim(),
        description: `Tier: ${tier || 'all'}, Min qty: ${minQuantity || 1}`,
        discount_type: selectedType === '% Discount' ? 'percent' : 'flat',
        discount_value: parsedDiscount,
        scope: 'all',
        starts_at: new Date(startDate).toISOString(),
        ends_at: new Date(endDate).toISOString(),
        is_active: true,
      });
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create promotion');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitle}>Add New Promotions</h2>
            <p className={styles.modalSubtitle}>Fill in the details below &middot; <span className={styles.required}>*</span> = Required</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Section 1: Basic Information */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <svg className={styles.sectionIconGreen} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              <h3 className={styles.sectionTitle}>1. Basic Information</h3>
            </div>
            <div className={styles.divider} />

            <div className={styles.row2}>
              <div className={styles.field}>
                <label className={styles.label}>Name <span className={styles.required}>*</span></label>
                <input className={styles.input} placeholder="e.g., Monster Energy Original" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Discount <span className={styles.required}>*</span></label>
                <input className={styles.input} placeholder="e.g., Monster" value={discount} onChange={(e) => setDiscount(e.target.value)} />
              </div>
            </div>

            <div className={styles.row2}>
              <div className={styles.field}>
                <label className={styles.label}>Start date<span className={styles.required}>*</span></label>
                <input className={styles.input} placeholder="e.g., MON-001-24" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                <span className={styles.hint}>Internal product code</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>End date <span className={styles.required}>*</span></label>
                <input className={styles.input} placeholder="e.g., 070847811800" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                <span className={styles.hint}>12-digit barcode number</span>
              </div>
            </div>

            <div className={styles.row2}>
              <div className={styles.field}>
                <label className={styles.label}>Tiers <span className={styles.required}>*</span></label>
                <div className={styles.selectWrapper}>
                  <select className={styles.select} value={tier} onChange={(e) => setTier(e.target.value)}>
                    <option value="">Select Category</option>
                    {tierOptions.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <svg className={styles.selectArrow} viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
                </div>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Min Quantity <span className={styles.required}>*</span></label>
                <div className={styles.selectWrapper}>
                  <select className={styles.select} value={minQuantity} onChange={(e) => setMinQuantity(e.target.value)}>
                    <option value="">Select Status</option>
                    <option value="1">1</option>
                    <option value="3">3</option>
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </select>
                  <svg className={styles.selectArrow} viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Promotion Type */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <svg className={styles.sectionIconYellow} viewBox="0 0 24 24" fill="currentColor"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>
              <h3 className={styles.sectionTitle}>2. Promotion Type</h3>
            </div>
            <div className={styles.divider} />

            <div className={styles.typeBox}>
              <div className={styles.typeGrid}>
                {promoTypeOptions.map((type) => (
                  <button
                    key={type}
                    className={`${styles.typeCard} ${selectedType === type ? styles.typeCardActive : ''}`}
                    onClick={() => setSelectedType(type)}
                  >
                    <span className={styles.typeLabel}>{type}</span>
                    <div className={`${styles.typeToggle} ${selectedType === type ? styles.typeToggleOn : ''}`}>
                      <span className={styles.typeToggleKnob} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {submitError && (
          <div style={{ margin: '12px 24px', padding: 12, background: '#FEF2F2', color: '#B91C1C', borderRadius: 6 }}>
            {submitError}
          </div>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={submitting}>Cancel</button>
          <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitting}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
            {submitting ? 'Adding…' : 'Add Promotions'}
          </button>
        </div>
      </div>
    </div>
  );
};
